import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrialPipeline, useTrials } from '../../api/funnel';
import { Page, PageHeader } from '../../components/Layout';
import { FilterBar, FilterSelect, SearchInput } from '../../components/FilterBar';
import { Pagination } from '../../components/Pagination';
import { TrialStageBadge, TRIAL_STAGE_LABEL } from '../../components/Badge';
import { UserCell } from '../../components/Avatar';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Spinner';
import { useDebounced } from '../../hooks/useDebounced';
import { formatMoney, formatRelative, formatDateTime } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { AdminTrial, TrialStage } from '../../types/api';
import { CreateFreeTrialModal } from './CreateFreeTrialModal';
import { AssignTutorModal } from './AssignTutorModal';
import { WEEKDAYS, formatPreferredSlot } from './weekdays';

/**
 * The stages worth showing as a board, left to right in the order a family
 * moves through them. `cancelled` and `expired` are deliberately absent:
 * they are not a stage anyone is working towards, and they are reachable
 * from the stage filter when support needs them.
 */
const BOARD: TrialStage[] = [
  'pending_payment',
  'awaiting_assignment',
  'assigned',
  'completed',
  'converted',
];

/** The two columns where a trial is waiting on *us*. */
const NEEDS_ACTION: TrialStage[] = ['awaiting_assignment', 'completed'];

export function TrialsPage() {
  const navigate = useNavigate();
  const [creatingFree, setCreatingFree] = useState(false);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<TrialStage | ''>('awaiting_assignment');
  const [weekday, setWeekday] = useState('');
  const [page, setPage] = useState(1);
  const [assigning, setAssigning] = useState<AdminTrial | null>(null);

  const debouncedSearch = useDebounced(search);

  const query = useMemo(
    () => ({
      page,
      limit: 20,
      stage: stage || undefined,
      weekday: weekday === '' ? undefined : Number(weekday),
      search: debouncedSearch.trim() || undefined,
    }),
    [page, stage, weekday, debouncedSearch],
  );

  const trials = useTrials(query);
  const pipeline = useTrialPipeline();
  const hasFilters = Boolean(debouncedSearch || weekday || stage);

  const resetFilters = () => {
    setSearch('');
    setStage('');
    setWeekday('');
    setPage(1);
  };

  const counts = pipeline.data?.byStage;

  return (
    <Page>
      <PageHeader
        title="Trials"
        description="Trial lessons, including complimentary introductions, and how many turn into a subscription."
        actions={<button type="button" className="btn btn-primary" onClick={() => setCreatingFree(true)}>Add free trial</button>}
      />

      {/* The board doubles as the stage filter: a column is the thing you
          click to work it, which is how an operator actually uses it. */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {BOARD.map((s) => {
          const count = counts?.[s] ?? 0;
          const selected = stage === s;
          const urgent = NEEDS_ACTION.includes(s) && count > 0;
          return (
            <button
              key={s}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setStage(selected ? '' : s);
                setPage(1);
              }}
              className={`panel px-3 py-2.5 text-left transition-colors ${
                selected ? 'ring-1 ring-accent' : 'hover:bg-surface-2'
              }`}
            >
              <span className="flex items-baseline gap-1.5">
                <span className="text-xl font-semibold tabular-nums text-ink">
                  {pipeline.isLoading ? '—' : count}
                </span>
                {urgent && <span className="pulse-dot" aria-hidden="true" />}
              </span>
              <span className="mt-0.5 block text-xs text-ink-3">
                {TRIAL_STAGE_LABEL[s]}
              </span>
            </button>
          );
        })}
      </div>

      {pipeline.data && (
        <p className="mb-4 text-xs text-ink-3">
          <span className="font-medium text-ink-2">
            {Math.round(pipeline.data.conversionRate * 100)}% convert
          </span>{' '}
          — of the trials actually taught, the share that went on to buy the
          bundle. Trial fees collected:{' '}
          {formatMoney(pipeline.data.trialRevenueCents, pipeline.data.currency)}.
        </p>
      )}

      <div className="panel overflow-hidden">
        <FilterBar onReset={resetFilters} hasFilters={hasFilters}>
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search the parent"
          />
          <FilterSelect
            label="Stage"
            value={stage}
            onChange={(v) => {
              setStage(v);
              setPage(1);
            }}
            placeholder="Any stage"
            options={(Object.keys(TRIAL_STAGE_LABEL) as TrialStage[]).map((s) => ({
              value: s,
              label: TRIAL_STAGE_LABEL[s],
            }))}
          />
          <FilterSelect
            label="Free on"
            value={weekday}
            onChange={(v) => {
              setWeekday(v);
              setPage(1);
            }}
            placeholder="Any day"
            options={WEEKDAYS.map((d, i) => ({ value: String(i), label: d }))}
          />
        </FilterBar>

        {trials.isError ? (
          <ErrorState
            message={trials.error instanceof ApiError ? trials.error.message : undefined}
            onRetry={() => void trials.refetch()}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Parent</th>
                    <th>Wants</th>
                    <th>Times offered</th>
                    <th>Stage</th>
                    <th>Tutor</th>
                    <th>Paid</th>
                    <th>Waiting</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {trials.isLoading ? (
                    <SkeletonRows rows={8} cols={8} />
                  ) : trials.data && trials.data.data.length > 0 ? (
                    trials.data.data.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <UserCell name={t.student.fullName} />
                          <span className="mt-0.5 block text-xs text-ink-3">
                            {t.student.gradeLevel ?? t.student.email}
                          </span>
                        </td>
                        <td className="text-xs text-ink-2">
                          <span className="block text-ink">{t.subject.nameEn}</span>
                          <span className="block text-ink-3">{t.bundle.nameEn}</span>
                        </td>
                        <td className="text-xs text-ink-2">
                          {t.preferredSlots.length === 0 ? (
                            '—'
                          ) : (
                            <span className="flex flex-wrap gap-1">
                              {t.preferredSlots.map((slot) => (
                                <span
                                  key={`${slot.weekday}-${slot.startLocal}`}
                                  className="badge bg-surface-2 text-ink-2"
                                >
                                  {formatPreferredSlot(slot)}
                                </span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td>
                          <TrialStageBadge stage={t.stage} />
                        </td>
                        <td className="text-xs text-ink-2">
                          {t.tutor ? (
                            <>
                              <span className="block text-ink">{t.tutor.fullName}</span>
                              {t.sessionStartTime && (
                                <span className="block text-ink-3">
                                  {formatDateTime(t.sessionStartTime)}
                                </span>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="whitespace-nowrap text-xs text-ink-2">
                          {t.amountCents === undefined
                            ? '—'
                            : formatMoney(t.amountCents, t.currency ?? 'SAR')}
                        </td>
                        <td className="text-xs text-ink-3">
                          <span title={new Date(t.createdAt).toLocaleString()}>
                            {formatRelative(t.createdAt)}
                          </span>
                        </td>
                        <td className="text-right">
                          {t.stage === 'awaiting_assignment' ? (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => setAssigning(t)}
                            >
                              Assign a tutor
                            </button>
                          ) : t.sessionId ? (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => navigate(`/sessions/${t.sessionId}`)}
                            >
                              View lesson
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8}>
                        <EmptyState
                          title={
                            stage === 'awaiting_assignment'
                              ? 'Nothing is waiting for a tutor'
                              : 'No trials match these filters'
                          }
                          description={
                            stage === 'awaiting_assignment'
                              ? 'Paid trial requests land here the moment a parent checks out.'
                              : 'Try a different stage, or clear the filters.'
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {trials.data && (
              <Pagination meta={trials.data.meta} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      {creatingFree && <CreateFreeTrialModal onClose={() => setCreatingFree(false)} onCreated={(trial) => {
        setCreatingFree(false);
        if (trial.sessionId) navigate(`/sessions/${trial.sessionId}`);
        else { setStage('assigned'); setSearch(trial.student.fullName); setPage(1); }
      }} />}
      {assigning && (
        <AssignTutorModal
          key={assigning.id}
          trial={assigning}
          onClose={() => setAssigning(null)}
        />
      )}
    </Page>
  );
}
